<template>
    <RedesignBaseCounterCard
        :widget="data"
        :card-text="cardText"
        :card-value="cardValue"
        :show-text="showText"
        :display-border="displayBorder"
        :selected-type="queueType"
        :icon="queueTypeData.icon"
        :icon-color="queueTypeData.color"
        :limit-status-type="notificationStatus"
        animation
        class="queue-counter-card"
    />
</template>

<script setup lang="ts">
/* Widget Queue Counter Card */
import RedesignBaseCounterCard from '@/components/RedesignComponents/Cards/RedesignBaseCounterCard.vue'
import { WidgetExtended } from '@/types/dashboard'
import { computed, ref, watch } from 'vue'
import {
    CALLERS_TYPE_CONFIG,
    CALLERS_NOT_VIRTUAL_TYPE_CONFIG,
    CALLERS_VIRTUAL_TYPE_CONFIG,
    MAXIMUM_VIRTUAL_WAITING_TYPE_CONFIG,
    MAXIMUM_NOT_VIRTUAL_WAITING_TYPE_CONFIG,
    isCounter,
    types,
} from '@/enum/queueCounters'
import { ObjectAnyType } from '@/types/generic'
import { isObject } from '@/util/objectUtil'
import i18n from '@/i18n'
import {
    removeSoundsFromQueueByIdWithHistoryKeep,
    updateSoundCurrentValue,
    updateTimerStateValue
} from '@/composables/useSoundsStoreData'
import { useTimer } from '@/composables/useTimer'
import { useSoundsThresholdNotification, warningThresholdValue, limitThresholdValue } from '@/composables/useSoundsThresholdNotification'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import useQueues from '@/composables/useQueues'
import { QUEUE_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import { currentAccountEntitiesQueues } from '@/composables/globalData/useEntitiesData'
const notificationProperty = 'max time'
const notificationCallersProperty = 'max callers'
const notificationTag = 'widget.soundBaseCardHtmlRedesign'

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
        showText?: boolean
        displayBorder?: boolean
        queue?: boolean
        queueType?: number
    }>(),
    {
        showText: true,
        displayBorder: true,
        queueType: 0
    })

const notificationStatus = ref('')

const {
    timerState,
    startTimer,
    stopTimer,
    timerDisplayTime,
    clearTimer
} = useTimer(false)

/*Composable*/
const { queues: allQueues } = useQueues()

/* Computed */
const widgetData = computed(() => {
    return props.data
})

const allFilteredQueues = computed(() => {
    return allQueues.value.filter(({ QueueID }) => {
        return !!currentAccountEntitiesQueues.value[QueueID]
    })
})

const { filteredItems: filteredQueue } = useEntityPositiveNegativeFilters(widgetData, QUEUE_LIST_PARAMETER_NAME, allFilteredQueues, 'QueueID')

const queueTypeData = computed(() => {
    return types[props.queueType]
})

const cardValue = computed(() => {
    switch (props.queueType) {
        case CALLERS_TYPE_CONFIG.id:
            return getQueueStats().queueCalls
        case CALLERS_VIRTUAL_TYPE_CONFIG.id:
            return getQueueStats().queueCallsVirtual
        case CALLERS_NOT_VIRTUAL_TYPE_CONFIG.id:
            return getQueueStats().queueCallsNotVirtual
        default:
            return timerDisplayTime.value
    }
})
const defaultCardText = computed(() => {
    return i18n.t(queueTypeData.value.text).toString()
})
const cardText = computed(() => {
    return props.data.WidgetLayout?.ContentTitle && isObject(props.data.WidgetLayout.ContentTitle) && (props.data.WidgetLayout.ContentTitle[props.queueType] || props.data.WidgetLayout.ContentTitle[props.queueType] === '') ? props.data.WidgetLayout.ContentTitle[props.queueType] : defaultCardText.value
})

const {
    getThresholdValue,
    getCurrentNotificationStatus,
    setupNotification: setupSoundNotification,
    reverseThreshold
} = useSoundsThresholdNotification(widgetData)

/* Methods */
function getQueueStats () {
    let minJoinTimeStamp = Date.now()
    let queueCalls = 0
    let queueCallsVirtual = 0
    let queueCallsNotVirtual = 0
    let minVirtualJoinTimeStamp: null | number = null
    let minNotVirtualJoinTimeStamp: null | number = null

    filteredQueue.value.forEach((queue: ObjectAnyType) => {
        queue.Calls.forEach((call: ObjectAnyType) => {
            if (call.JoinTimeStamp_UTC_CLIENT < minJoinTimeStamp) {
                minJoinTimeStamp = call.JoinTimeStamp_UTC_CLIENT
            }

            // Is virtual holder
            if ('VHJoinTimeStamp_UTC_CLIENT' in call && call.VHJoinTimeStamp_UTC_CLIENT) {
                queueCallsVirtual++
                if (minVirtualJoinTimeStamp === null || call.JoinTimeStamp_UTC_CLIENT < minVirtualJoinTimeStamp) {
                    minVirtualJoinTimeStamp = call.JoinTimeStamp_UTC_CLIENT
                }
            } else {
                queueCallsNotVirtual++
                if(minNotVirtualJoinTimeStamp === null || call.JoinTimeStamp_UTC_CLIENT < minNotVirtualJoinTimeStamp) {
                    minNotVirtualJoinTimeStamp = call.JoinTimeStamp_UTC_CLIENT
                }

            }
            queueCalls++
        })
    })

    return {
        queueCalls,
        minJoinTimeStamp,
        queueCallsVirtual,
        queueCallsNotVirtual,
        minVirtualJoinTimeStamp: minVirtualJoinTimeStamp === null ? 0: minVirtualJoinTimeStamp,
        minNotVirtualJoinTimeStamp: minNotVirtualJoinTimeStamp === null ? 0: minNotVirtualJoinTimeStamp,
    }
}

function getInitialQueueTime () {
    if (isCounter(props.queueType)) {
        return 0
    }
    const { queueCalls, minJoinTimeStamp, minVirtualJoinTimeStamp, minNotVirtualJoinTimeStamp } = getQueueStats()

    if (queueCalls === 0) {
        return 0
    }
    switch (props.queueType) {
        case MAXIMUM_VIRTUAL_WAITING_TYPE_CONFIG.id:
            return minVirtualJoinTimeStamp
        case MAXIMUM_NOT_VIRTUAL_WAITING_TYPE_CONFIG.id:
            return minNotVirtualJoinTimeStamp
        default:
            return minJoinTimeStamp
    }
}

function setCounterState () {
    const callCount = getQueueStats().queueCalls
    stopTimer()
    const timerValue = getInitialQueueTime()

    if (isCounter(props.queueType)) {
        let notificationCallCount = 0
        const queueStats = getQueueStats()

        switch (props.queueType) {
            case CALLERS_TYPE_CONFIG.id:
                notificationCallCount = queueStats.queueCalls
                break
            case CALLERS_VIRTUAL_TYPE_CONFIG.id:
                notificationCallCount = queueStats.queueCallsVirtual
                break
            case CALLERS_NOT_VIRTUAL_TYPE_CONFIG.id:
                notificationCallCount = queueStats.queueCallsNotVirtual
                break
            default:
                notificationStatus.value = ''
        }

        handleCallersNotification(notificationCallCount)
        return
    }

    if (callCount !== 0 && timerValue) {
        startTimer({ value: timerValue })

        if (notificationStatus.value) {
            updateTimerStateValue(`${props.data.WidgetID}`, timerValue)
        }
    } else if (!callCount && !timerValue && timerState.value) {
        clearTimer()
    }
}

const updateNotification = (val: number, type: string, id: string) => {
    const notificationData = {
        notification_value: getThresholdValue(type),
        entity_name: cardText.value,
        current_value: `${cardValue.value}.`,
        property: notificationCallersProperty,
        type: type
    }
    const notificationHtml =  i18n.t(notificationTag, { ...notificationData })
    updateSoundCurrentValue(id, notificationHtml)
}

const handleCallersNotification = (val: number) => {
    const currentNotificationStatus = getCurrentNotificationStatus(val, reverseThreshold.value)

    if (!currentNotificationStatus && !notificationStatus.value) return
    if (!currentNotificationStatus) {
        notificationStatus.value = ''
        removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
        return
    }
    if(currentNotificationStatus === notificationStatus.value) {
        updateNotification(val, currentNotificationStatus, `${props.data.WidgetID}`)
        return
    }
    removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
    const shouldSoundNotBePlayed = notificationStatus.value === limitThresholdValue && currentNotificationStatus === warningThresholdValue
    notificationStatus.value = currentNotificationStatus

    setupSoundNotification({
        entityName: cardText.value,
        property: notificationCallersProperty,
        soundPlayed: shouldSoundNotBePlayed,
        currentValue: `${cardValue.value}.`,
        id: `${props.data.WidgetID}`,
        notificationTag,
        type: currentNotificationStatus,
    })
}

/* Watcher */
watch(
    () => props.queueType,
    () => {
        setCounterState()
    },
    { immediate: true }
)
watch(
    filteredQueue,
    () => {
        setCounterState()
    }, { deep: true }
)

const evaluateTimerNotification = () => {
    const val = timerState.value
    const currentNotificationStatus = getCurrentNotificationStatus(val, reverseThreshold.value)

    if (!currentNotificationStatus && !notificationStatus.value) return

    if (!currentNotificationStatus) {
        notificationStatus.value = ''
        removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
        return
    }

    if (currentNotificationStatus === notificationStatus.value) {
        return
    }
    removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
    const shouldSoundNotBePlayed = notificationStatus.value === limitThresholdValue && currentNotificationStatus === warningThresholdValue

    notificationStatus.value = currentNotificationStatus

    setupSoundNotification({
        entityName: cardText.value,
        property: notificationProperty,
        soundPlayed: shouldSoundNotBePlayed,
        currentValue: '',
        id: `${props.data.WidgetID}`,
        notificationTag,
        type: currentNotificationStatus,
        timerStartValue: getInitialQueueTime()
    })
}

watch(() => timerState.value, () => {
    evaluateTimerNotification()
})

watch(() => widgetData.value, () => {
    evaluateTimerNotification()
})
</script>
